import React, {
  SyntheticEvent, useCallback, useEffect, useState,
} from 'react';
import searchFill from '@iconify/icons-eva/search-fill';
import {
  Toolbar, Grid, TextField,
  Autocomplete, InputAdornment, Typography,
} from '@material-ui/core';
import {
  LoadingButton, LocalizationProvider,
} from '@material-ui/lab';
import DateFnsUtils from '@material-ui/lab/AdapterDateFns';
import { styled } from '@material-ui/core/styles';
import { observer } from 'mobx-react-lite';
import { Box } from '@material-ui/system';
import { Icon } from '@iconify/react';
import { useDebounce } from 'use-debounce';
import { Option } from '../../../types';
import { useStore } from '../../../hooks';
import { CustomDatePicker } from '../../../components/CustomDatePicker';
import { getFileConsumerReport } from '../../../services';

const RootStyle = styled(Toolbar)(({ theme }) => ({
  minHeight: '0px !important',
  display: 'flex',
  justifyContent: 'space-between',
  padding: theme.spacing(0, 3),
}));

export const ConsumerToolbar: React.FC<{ page: number }> = observer(({ page }) => {
  const {
    reports: {
      fetchAllConsumerReport,

      allConsumerReport: { items, meta },
    },
    serviceModels: { modelsAutoComplete: serviceModels },
    countriesModel: { countriesAutoComplete: countries },

  } = useStore();

  // const theme = useTheme();

  const [searchEmail, setSearchEmail] = useState('');

  const [searchCountry, setSearchCountry] = useState<number[] | null>(null);
  const [searchModel, setSearchModel] = useState<number[] | null>(null);

  const [datePurchaseFrom, setDatePurchaseFrom] = useState<string | null>(null);
  const [datePurchaseTo, setDatePurchaseTo] = useState<string | null>(null);
  const [createdAtFrom, setCreatedAtFrom] = useState<string | null>(null);
  const [createdAtTo, setCreatedAtTo] = useState<string | null>(null);
  const [isGeneratedFile, setIsGeneratedFile] = useState<boolean>(true);

  const [debouncedEmail] = useDebounce(searchEmail, 400);

  const fetchReport = useCallback(() => {
    fetchAllConsumerReport({
      email: debouncedEmail,
      countryId: searchCountry,
      modelId: searchModel,
      datePurchaseFrom,
      datePurchaseTo,
      createdAtFrom,
      createdAtTo,
      page,
    });
  }, [
    debouncedEmail,
    searchCountry,
    searchModel,
    datePurchaseFrom,
    datePurchaseTo,
    createdAtFrom,
    createdAtTo,
    page,
  ]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const onFilterModel = useCallback((newFilterModel: number[] | null):void => {
    setSearchModel(newFilterModel);
  }, []);
  const onFilterCountry = useCallback((newFilterCountry: number[] | null):void => {
    setSearchCountry(newFilterCountry);
  }, []);

  const handleModelsChange = useCallback((event: SyntheticEvent, values): void => {
    const customModels = values.map((item) => item.value);
    onFilterModel(customModels);
  }, []);

  const handleCountriesChange = useCallback((event: SyntheticEvent, values): void => {
    const customCountries = values.map((item) => item.value);
    onFilterCountry(customCountries);
  }, []);

  const handleEmailChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setSearchEmail(event.target.value);
    },
    [],
  );

  const handleGenerateReportFile = useCallback(async (): Promise<void> => {
    setIsGeneratedFile(false);
    const result = await getFileConsumerReport({
      email: debouncedEmail,
      countryId: searchCountry,
      modelId: searchModel,
      datePurchaseFrom,
      datePurchaseTo,
      createdAtFrom,
      createdAtTo,
    });
    if (result) {
      setIsGeneratedFile(true);
      const buffer = new Int8Array(result.buffer.data);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(new Blob([buffer], { type: result.mimeType }));
      a.setAttribute('download', 'consumer_report.xlsx');
      a.click();
    }
  }, [
    debouncedEmail,
    searchCountry,
    searchModel,
    datePurchaseFrom,
    datePurchaseTo,
    createdAtFrom,
    createdAtTo,
  ]);

  return (
    <>
      <RootStyle sx={{ paddingTop: '25px', paddingBottom: '25px', paddingRight: '0px !important' }}>
        <Grid
          container
          xs={12}
          spacing={3}
        >
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              fullWidth
              options={serviceModels}
              onChange={handleModelsChange}
              getOptionLabel={(option: Option): string => option.label}
              renderInput={(autocompleteParams): JSX.Element => <TextField {...autocompleteParams} label="Model" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple
              size="small"
              fullWidth
              options={countries}
              onChange={handleCountriesChange}
              getOptionLabel={(option: Option): string => option.label}
              renderInput={(autocompleteParams): JSX.Element => <TextField {...autocompleteParams} label="Country" />}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              size="small"
              fullWidth
              label="Email"
              value={searchEmail}
              onChange={handleEmailChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </RootStyle>

      <RootStyle sx={{ paddingTop: '25px', paddingBottom: '25px', paddingRight: '0px !important' }}>
        <LocalizationProvider dateAdapter={DateFnsUtils}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <CustomDatePicker
                label="Purchase date from"
                value={datePurchaseFrom}
                handleChange={setDatePurchaseFrom}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomDatePicker
                label="Purchase date to"
                value={datePurchaseTo}
                handleChange={setDatePurchaseTo}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </RootStyle>

      <RootStyle sx={{ paddingTop: '25px', paddingBottom: '25px', paddingRight: '0px !important' }}>
        <LocalizationProvider dateAdapter={DateFnsUtils}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <CustomDatePicker
                label="Registration date from"
                value={createdAtFrom}
                handleChange={setCreatedAtFrom}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <CustomDatePicker
                label="Registration date to"
                value={createdAtTo}
                handleChange={setCreatedAtTo}
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </RootStyle>

      <RootStyle sx={{ paddingTop: '25px', paddingBottom: '25px', paddingRight: '0px !important' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <LoadingButton
              disabled={!items.length}
              variant="contained"
              loading={!isGeneratedFile}
              onClick={handleGenerateReportFile}
            >
              Generate Excel file
            </LoadingButton>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body1">
              Total consumers:
              {' '}
              {meta.totalItems}
            </Typography>
          </Grid>
        </Grid>
      </RootStyle>
    </>
  );
});
